import React from "react";
import PropTypes from "prop-types";
import {
	Button,
	Container,
	Grid,
	Paper,
	withStyles,
	FormControl,
	MenuItem,
	Typography,
} from "@material-ui/core";
import { withSnackbar } from "notistack";

import { CssLabel, CssSelect } from "./style";
import { LeaderboardForm, validateLeaderboardForm } from "./LeaderboardBots";
import { LeagueForm, validateLeagueForm, LeagueReceiptExpireForm, validateLeagueReceiptExpireForm, LeagueChangeForm, validateLeagueChangeForm } from "./LeagueBots";
import { TournamentForm, validateTournamentForm } from "./TournamentBots";
import { LeagueRankForm, validateLeagueRankForm } from "./UpdateLeagueRank";
import { LuckyDiceForm, validateLuckyDiceForm } from "./addLuckyDice";
import { postBotsUpdate } from "./apiHandler";

const useStyles = (theme) => ({
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paperCol: {
		padding: theme.spacing(2),
		display: "flex",
		overflow: "auto",
		flexDirection: "column",
	},
	updateButton: {
		backgroundColor: "white",
		marginTop: 10,
		marginBottom: 10,
		marginRight: 10,
	},
	startPaper: {
		padding: theme.spacing(2),
		display: "flex",
		backgroundColor: "#546bd6",
		justifyContent: "center",
	},
	inputBar: {
		display: "flex",
		width: "100%",
		flexDirection: "column",
	},
	controlRow: {
		display: "flex",
		width: "100%",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(2),
	},
	inputRow: {
		display: "flex",
		width: "100%",
		justifyContent: "flex-start",
		alignItems: "center",
		flexWrap: "wrap",
	},
	formControl: {
		margin: theme.spacing(2),
		minWidth: 200,
	},
	textField: {
		margin: theme.spacing(2),
		width: 200,
	},
	sectionTitle: {
		color: "white",
		margin: theme.spacing(2),
		fontWeight: "bold",
	},
});

const BotsView = ({ classes, dev, enqueueSnackbar }) => {
	const [selectedOption, setSelectedOption] = React.useState("leaderboard");
	const [loading, setLoading] = React.useState(false);

	// Leaderboard form states
	const [botCount, setBotCount] = React.useState("");
	const [minRange, setMinRange] = React.useState("");
	const [maxRange, setMaxRange] = React.useState("");

	// League form states
	const [leagueCount, setLeagueCount] = React.useState("");
	const [userId, setUserId] = React.useState("");
	const [leagueType, setLeagueType] = React.useState("lg");
	const [isGoldRush, setIsGoldRush] = React.useState(false);

	// Tournament form states
	const [tournamentId, setTournamentId] = React.useState("");
	const [userCount, setUserCount] = React.useState("");

	// League Rank form states
	const [newRank, setNewRank] = React.useState("");

	// Lucky Dice form state
	const [diceCount, setDiceCount] = React.useState("");

	// League Change form states
	const [leagueChangeLeague, setLeagueChangeLeague] = React.useState(1);
	const [leagueChangeType, setLeagueChangeType] = React.useState(1);

	// Reset all form fields
	const resetForm = () => {
		setBotCount("");
		setMinRange("");
		setMaxRange("");
		setLeagueCount("");
		setUserId("");
		setLeagueType("lg");
		setIsGoldRush(false);
		setLeagueChangeLeague(1);
		setLeagueChangeType(1);
		setTournamentId("");
		setUserCount("");
		setNewRank("");
		setDiceCount("");
	};

	// Get appropriate payload based on current selection
	const getPayload = () => {
		switch (selectedOption) {
			case "leaderboard":
				return {
					user_id: userId,
					bot_count: parseInt(botCount, 10),
					min_range: parseInt(minRange, 10),
					max_range: parseInt(maxRange, 10),
				};
			case "league":
				return {
					bot_count: parseInt(leagueCount, 10),
					user_id: userId,
					league_type: leagueType,
					is_gold_rush: isGoldRush,
				};
			case "leagueReceiptExpire":
				return {
					userID: userId,
				};
			case "leagueChange":
				return {
					user_id: userId,
					league: leagueChangeLeague,
					league_type: leagueChangeType,
					is_gold_rush: isGoldRush,
				};
			case "tournament":
				return {
					tournament_id: tournamentId,
					user_count: parseInt(userCount, 10),
				};
			case "leagueRank":
				return {
					user_id: userId,
					new_rank: parseInt(newRank, 10),
				};
			case "luckyDice":
				return {
					user_id: userId,
					count: parseInt(diceCount, 10),
				};
			default:
				return {};
		}
	};

	// Check if form is valid based on current selection
	const isFormValid = () => {
		switch (selectedOption) {
			case "leaderboard":
				return validateLeaderboardForm({
					userId,
					botCount,
					minRange,
					maxRange,
				});
			case "league":
				return validateLeagueForm({
					leagueCount,
					userId,
				});
			case "tournament":
				return validateTournamentForm({
					tournamentId,
					userCount,
				});
			case "leagueRank":
				return validateLeagueRankForm({
					userId,
					newRank,
				});
			case "luckyDice":
				return validateLuckyDiceForm({
					userId,
					diceCount,
				});
			case "leagueReceiptExpire":
				return validateLeagueReceiptExpireForm({
					userId,
				});
			case "leagueChange":
				return validateLeagueChangeForm({
					userId,
					leagueChangeLeague,
				});
			default:
				return false;
		}
	};

	// Submit form data
	const handleSubmit = () => {
		const payload = getPayload();
		const callbacks = { setLoading, resetForm, enqueueSnackbar };
		postBotsUpdate(dev, selectedOption, payload, callbacks);
	};

	// Render form fields based on selected option
	const renderFormFields = () => {
		switch (selectedOption) {
			case "leaderboard":
				return (
					<LeaderboardForm
						classes={classes}
						userId={userId}
						setUserId={setUserId}
						botCount={botCount}
						setBotCount={setBotCount}
						minRange={minRange}
						setMinRange={setMinRange}
						maxRange={maxRange}
						setMaxRange={setMaxRange}
					/>
				);
			case "league":
				return (
					<LeagueForm
						classes={classes}
						leagueCount={leagueCount}
						setLeagueCount={setLeagueCount}
						userId={userId}
						setUserId={setUserId}
						leagueType={leagueType}
						setLeagueType={setLeagueType}
						isGoldRush={isGoldRush}
						setIsGoldRush={setIsGoldRush}
					/>
				);
			case "leagueReceiptExpire":
				return (
					<LeagueReceiptExpireForm
						classes={classes}
						userId={userId}
						setUserId={setUserId}
					/>
				);
			case "leagueChange":
				return (
					<LeagueChangeForm
						classes={classes}
						userId={userId}
						setUserId={setUserId}
						leagueChangeLeague={leagueChangeLeague}
						setLeagueChangeLeague={setLeagueChangeLeague}
						leagueChangeType={leagueChangeType}
						setLeagueChangeType={setLeagueChangeType}
						isGoldRush={isGoldRush}
						setIsGoldRush={setIsGoldRush}
					/>
				);
			case "tournament":
				return (
					<TournamentForm
						classes={classes}
						tournamentId={tournamentId}
						setTournamentId={setTournamentId}
						userCount={userCount}
						setUserCount={setUserCount}
					/>
				);
			case "leagueRank":
				return (
					<LeagueRankForm
						classes={classes}
						userId={userId}
						setUserId={setUserId}
						newRank={newRank}
						setNewRank={setNewRank}
					/>
				);
			case "luckyDice":
				return (
					<LuckyDiceForm
						classes={classes}
						userId={userId}
						setUserId={setUserId}
						diceCount={diceCount}
						setDiceCount={setDiceCount}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<Container maxWidth="lg" className={classes.container}>
			<Grid container spacing={3}>
				<Grid item xs={12} />
			</Grid>
			<Grid container spacing={3} style={{ marginTop: 30 }}>
				<Paper className={classes.startPaper} style={{ width: "100%" }}>
					<Grid container spacing={3}>
						<Grid item xs={12} className={classes.inputBar}>
							<div className={classes.controlRow}>
								<FormControl className={classes.formControl}>
									<CssLabel>Select Script Type</CssLabel>
									<CssSelect
										value={selectedOption}
										onChange={(e) => {
											setSelectedOption(e.target.value);
											resetForm();
										}}
									>
										<MenuItem value="leaderboard">Leaderboard Bots</MenuItem>
										<MenuItem value="league">League Bots</MenuItem>
										<MenuItem value="tournament">Tournament</MenuItem>
										<MenuItem value="leagueRank">Change League Rank</MenuItem>
										<MenuItem value="luckyDice">Add Lucky Dice</MenuItem>
									<MenuItem value="leagueReceiptExpire">League Receipt Expire</MenuItem>
									<MenuItem value="leagueChange">League Change</MenuItem>
									</CssSelect>
								</FormControl>
								<div>
									<Button
										className={classes.updateButton}
										variant="contained"
										disabled={!isFormValid() || loading}
										onClick={handleSubmit}
									>
										{loading ? "Updating..." : "Update"}
									</Button>
									<Button
										className={classes.updateButton}
										variant="contained"
										onClick={resetForm}
										disabled={loading}
									>
										Reset
									</Button>
								</div>
							</div>
							{renderFormFields()}
						</Grid>
					</Grid>
				</Paper>
			</Grid>
		</Container>
	);
};

BotsView.propTypes = {
	classes: PropTypes.object.isRequired,
	dev: PropTypes.string.isRequired,
	enqueueSnackbar: PropTypes.func.isRequired,
};

export default withStyles(useStyles)(withSnackbar(BotsView));
